/**
 * 近期使用智能体状态管理Store
 * 使用Zustand管理近期使用的智能体，支持数据持久化
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AgentRes } from '@/api/types/agent.type'

/**
 * 近期使用智能体接口定义
 */
interface RecentAgent {
  /** 智能体ID */
  id: string
  /** 智能体名称 */
  name: string
  /** 智能体图标 */
  icon?: string
  /** 最后使用时间 */
  lastUsed: number
}

/**
 * 近期使用智能体状态接口定义
 */
interface RecentAgentsState {
  /** 近期使用的智能体列表 */
  recentAgents: RecentAgent[]

  /** 最大保存数量 */
  maxCount: number

  /**
   * 添加智能体到近期使用列表
   * @param agent 智能体信息
   */
  addRecentAgent: (agent: AgentRes) => void

  /**
   * 从近期使用列表中删除智能体
   * @param agentId 智能体ID
   */
  removeRecentAgent: (agentId: string) => void

  /**
   * 清空近期使用列表
   */
  clearRecentAgents: () => void

  /**
   * 获取近期使用的智能体列表
   * @returns 按最后使用时间排序的智能体列表
   */
  getRecentAgents: () => RecentAgent[]

  /** 重置store到初始状态 */
  reset: () => void
}

/**
 * 初始状态
 */
const initialState = {
  recentAgents: [],
  maxCount: 10, // 最多保存10个近期使用的智能体
}

/**
 * 近期使用智能体状态Store
 * 支持本地存储持久化，刷新页面后数据会自动恢复
 */
export const useRecentAgentsStore = create<RecentAgentsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      addRecentAgent: (agent) => {
        const { recentAgents, maxCount } = get()
        const now = Date.now()

        // 检查是否已存在该智能体
        const existingIndex = recentAgents.findIndex(
          (item) => item.id === agent.id
        )

        let newRecentAgents: RecentAgent[]

        if (existingIndex !== -1) {
          // 如果已存在，更新使用时间并移到最前面
          newRecentAgents = [
            {
              id: agent.id || '',
              name: agent.name || '',
              icon: agent.icon,
              lastUsed: now,
            },
            ...recentAgents.filter((_, index) => index !== existingIndex),
          ]
        } else {
          // 如果不存在，添加到最前面
          const newAgent: RecentAgent = {
            id: agent.id || '',
            name: agent.name || '',
            icon: agent.icon,
            lastUsed: now,
          }

          newRecentAgents = [newAgent, ...recentAgents]

          // 如果超过最大数量，删除最旧的
          if (newRecentAgents.length > maxCount) {
            newRecentAgents = newRecentAgents.slice(0, maxCount)
          }
        }

        set({ recentAgents: newRecentAgents })
      },

      removeRecentAgent: (agentId) => {
        const { recentAgents } = get()
        const newRecentAgents = recentAgents.filter(
          (agent) => agent.id !== agentId
        )
        set({ recentAgents: newRecentAgents })
      },

      clearRecentAgents: () => {
        set({ recentAgents: [] })
      },

      getRecentAgents: () => {
        const { recentAgents } = get()
        // 按最后使用时间倒序排列（最近使用的在前面）
        return [...recentAgents].sort((a, b) => b.lastUsed - a.lastUsed)
      },

      reset: () => set(initialState),
    }),
    {
      name: 'recent-agents-store', // 本地存储的键名
      partialize: (state) => ({
        recentAgents: state.recentAgents,
        maxCount: state.maxCount,
      }), // 指定需要持久化的状态字段
    }
  )
)
